import React from 'react';
import { Plus, Trash2, Upload, Eye, Settings, Palette } from 'lucide-react';
import ImageUploader from '../ImageUploader';
const PopupForm = ({ section, setSection }) => {
  // Helper function to update content
  const updateContent = (field, value) => {
    setSection(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [field]: value
      }
    }));
  }; 
   
  // Helper to add image to gallery
  const addImage = () => {
    const newImage = {
      url: '',
      alt: '',
      caption: ''
    };
    
    updateContent('images', [...(section.content.images || []), newImage]);
  };

  // Helper to remove image from gallery
  const removeImage = (index) => {
    const updatedImages = [...(section.content.images || [])];
    updatedImages.splice(index, 1);
    updateContent('images', updatedImages);
  };

  // Helper to update specific image
  const updateImage = (index, field, value) => {
    const updatedImages = [...(section.content.images || [])];
    updatedImages[index] = {
      ...updatedImages[index],
      [field]: value
    };
    updateContent('images', updatedImages);
  };

  const { content } = section;

  return (
    <div className="space-y-6">
      {/* Trigger Settings */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <Settings className="w-4 h-4 mr-2" />
          Trigger Settings
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Trigger Type</label>
            <select
              value={content.triggerType || 'button'}
              onChange={(e) => updateContent('triggerType', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="button">Button Trigger</option>
              <option value="image">Image Trigger</option>
              <option value="auto">Auto Trigger (Time-based)</option>
              <option value="scroll">Scroll Trigger</option>
              <option value="exit">Exit Intent</option>
              <option value="hover">Hover Trigger</option>
            </select>
          </div>

          {content.triggerType === 'button' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Button Text</label>
                <input
                  type="text"
                  value={content.triggerText || ''}
                  onChange={(e) => updateContent('triggerText', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Open Popup"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Style</label>
                <select
                  value={content.triggerButtonStyle || 'primary'}
                  onChange={(e) => updateContent('triggerButtonStyle', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="outline">Outline</option>
                  <option value="link">Link</option>
                  <option value="gradient">Gradient</option>
                  <option value="ghost">Ghost</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Size</label>
                <select
                  value={content.triggerButtonSize || 'medium'}
                  onChange={(e) => updateContent('triggerButtonSize', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="xs">Extra Small</option>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="xl">Extra Large</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Position</label>
                <select
                  value={content.triggerButtonPosition || 'inline'}
                  onChange={(e) => updateContent('triggerButtonPosition', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="inline">Inline</option>
                  <option value="center">Center</option>
                  <option value="left">Left Aligned</option>
                  <option value="right">Right Aligned</option>
                  <option value="fixed-bottom-right">Fixed Bottom Right</option>
                  <option value="fixed-bottom-left">Fixed Bottom Left</option>
                  <option value="fixed-top-right">Fixed Top Right</option>
                  <option value="fixed-top-left">Fixed Top Left</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Color</label>
                <input
                  type="color"
                  value={content.triggerButtonColor || '#3b82f6'}
                  onChange={(e) => updateContent('triggerButtonColor', e.target.value)}
                  className="w-full p-1 border rounded h-10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Text Color</label>
                <input
                  type="color"
                  value={content.triggerButtonTextColor || '#ffffff'}
                  onChange={(e) => updateContent('triggerButtonTextColor', e.target.value)}
                  className="w-full p-1 border rounded h-10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Hover Color</label>
                <input
                  type="color"
                  value={content.triggerButtonHoverColor || '#2563eb'}
                  onChange={(e) => updateContent('triggerButtonHoverColor', e.target.value)}
                  className="w-full p-1 border rounded h-10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Border Radius</label>
                <input
                  type="text"
                  value={content.triggerButtonBorderRadius || '6px'}
                  onChange={(e) => updateContent('triggerButtonBorderRadius', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="6px, 50%, 0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Button Shadow</label>
                <select
                  value={content.triggerButtonShadow || 'none'}
                  onChange={(e) => updateContent('triggerButtonShadow', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="none">No Shadow</option>
                  <option value="small">Small Shadow</option>
                  <option value="medium">Medium Shadow</option>
                  <option value="large">Large Shadow</option>
                  <option value="custom">Custom Shadow</option>
                </select>
              </div>

              {content.triggerButtonShadow === 'custom' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Custom Shadow CSS</label>
                  <input
                    type="text"
                    value={content.triggerButtonCustomShadow || ''}
                    onChange={(e) => updateContent('triggerButtonCustomShadow', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="0 4px 6px rgba(0, 0, 0, 0.1)"
                  />
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="triggerButtonFullWidth"
                  checked={content.triggerButtonFullWidth || false}
                  onChange={(e) => updateContent('triggerButtonFullWidth', e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="triggerButtonFullWidth" className="text-sm font-medium">
                  Full Width Button
                </label>
              </div>
            </>
          )}

          {content.triggerType === 'image' && (
            <>
             <div>
  <label className="block text-sm font-medium mb-2">Trigger Image URL</label>
  <input
    type="url"
    value={content.triggerImage || ''}
    onChange={(e) => updateContent('triggerImage', e.target.value)}
    className="w-full p-2 border rounded"
    placeholder="https://example.com/trigger-image.jpg"
  />
  <ImageUploader 
    onImageUpload={(url) => updateContent('triggerImage', url)}
    pageSlug="popup-images" 
  />
</div>

              <div>
                <label className="block text-sm font-medium mb-2">Image Hover Effect</label>
                <select
                  value={content.triggerImageHoverEffect || 'opacity'}
                  onChange={(e) => updateContent('triggerImageHoverEffect', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="none">No Effect</option>
                  <option value="opacity">Fade</option>
                  <option value="scale">Scale</option>
                  <option value="brightness">Brightness</option>
                  <option value="grayscale">Grayscale</option>
                  <option value="blur">Blur</option>
                  <option value="rotate">Rotate</option>
                </select>
              </div>
            </>
          )}

          {content.triggerType === 'auto' && (
            <>
              <div>
  <label className="block text-sm font-medium mb-2">Auto Trigger Delay (seconds)</label>
  <input
    type="number"
    value={content.autoTriggerDelay ? content.autoTriggerDelay / 1000 : 3}
    onChange={(e) => updateContent('autoTriggerDelay', parseInt(e.target.value) * 1000)}
    className="w-full p-2 border rounded"
    min="0"
    step="0.5"
  />
</div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoTriggerOnce"
                  checked={content.autoTriggerOnce !== false}
                  onChange={(e) => updateContent('autoTriggerOnce', e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="autoTriggerOnce" className="text-sm font-medium">
                  Show only once per session
                </label>
              </div>
            </>
          )}

          {content.triggerType === 'scroll' && (
            <div>
              <label className="block text-sm font-medium mb-2">Scroll Percentage (%)</label>
              <input
                type="number"
                value={content.scrollTriggerPercentage || 50}
                onChange={(e) => updateContent('scrollTriggerPercentage', parseInt(e.target.value))}
                className="w-full p-2 border rounded"
                min="0"
                max="100"
              />
            </div>
          )}

          {content.triggerType === 'hover' && (
            <div>
              <label className="block text-sm font-medium mb-2">Hover Target Element</label>
              <input
                type="text"
                value={content.hoverTargetSelector || ''}
                onChange={(e) => updateContent('hoverTargetSelector', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder=".class-name or #element-id"
              />
            </div>
          )}
        </div>
      </div>

      {/* Popup Content */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <Eye className="w-4 h-4 mr-2" />
          Popup Content
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Popup Type</label>
            <select
              value={content.popupType || 'content'}
              onChange={(e) => updateContent('popupType', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="content">Rich Content</option>
              <option value="image">Single Image</option>
              <option value="video">Video Player</option>
              <option value="gallery">Image Gallery</option>
              <option value="iframe">Embedded Content (iFrame)</option>
              <option value="form">Contact Form</option>
            </select>
          </div>

          {content.popupType === 'content' && (
  <>
    <div>
      <label className="block text-sm font-medium mb-2">Popup Title</label>
      <input
        type="text"
        value={content.popupTitle || ''}
        onChange={(e) => updateContent('popupTitle', e.target.value)}
        className="w-full p-2 border rounded"
        placeholder="Popup Title"
      />
    </div>

    <div>
      <label className="block text-sm font-medium mb-2">Content Type</label>
      <select
        value={content.contentLayout || 'text-only'}
        onChange={(e) => updateContent('contentLayout', e.target.value)}
        className="w-full p-2 border rounded"
      >
        <option value="text-only">Text Only</option>
        <option value="text-with-image">Text with Image</option>
        <option value="text-with-video">Text with Video</option>
        <option value="image-text-split">Image & Text Split</option>
        <option value="video-text-split">Video & Text Split</option>
      </select>
    </div>

    {(content.contentLayout === 'text-with-image' || content.contentLayout === 'image-text-split') && (
      <>
        <div>
          <label className="block text-sm font-medium mb-2">Content Image</label>
          <input
            type="url"
            value={content.contentImage || ''}
            onChange={(e) => updateContent('contentImage', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="https://example.com/image.jpg"
          />
          <ImageUploader 
            onImageUpload={(url) => updateContent('contentImage', url)}
            pageSlug="popup-content" 
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Image Width</label>
            <input
              type="text"
              value={content.contentImageWidth || '300px'}
              onChange={(e) => updateContent('contentImageWidth', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="300px, 50%, auto"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Image Position</label>
            <select
              value={content.contentImagePosition || 'left'}
              onChange={(e) => updateContent('contentImagePosition', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="left">Left</option>
              <option value="right">Right</option>
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Image Border Radius</label>
          <input
            type="text"
            value={content.contentImageBorderRadius || '8px'}
            onChange={(e) => updateContent('contentImageBorderRadius', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="8px, 50%, 0"
          />
        </div>
      </>
    )}

    {(content.contentLayout === 'text-with-video' || content.contentLayout === 'video-text-split') && (
      <>
        <div>
          <label className="block text-sm font-medium mb-2">Content Video URL</label>
          <input
            type="url"
            value={content.contentVideo || ''}
            onChange={(e) => updateContent('contentVideo', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="https://example.com/video.mp4"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Video Width</label>
            <input
              type="text"
              value={content.contentVideoWidth || '400px'}
              onChange={(e) => updateContent('contentVideoWidth', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="400px, 50%, auto"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Video Position</label>
            <select
              value={content.contentVideoPosition || 'left'}
              onChange={(e) => updateContent('contentVideoPosition', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="left">Left</option>
              <option value="right">Right</option>
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="contentVideoAutoplay"
              checked={content.contentVideoAutoplay || false}
              onChange={(e) => updateContent('contentVideoAutoplay', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="contentVideoAutoplay" className="text-sm font-medium">
              Auto Play
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="contentVideoControls"
              checked={content.contentVideoControls !== false}
              onChange={(e) => updateContent('contentVideoControls', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="contentVideoControls" className="text-sm font-medium">
              Show Controls
            </label>
          </div>
        </div>
      </>
    )}

    <div>
      <label className="block text-sm font-medium mb-2">Text Content (HTML)</label>
      <textarea
        value={content.popupContent || ''}
        onChange={(e) => updateContent('popupContent', e.target.value)}
        className="w-full p-2 border rounded"
        rows="8"
        placeholder="<p>Your popup content here...</p>"
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-2">Content Text Color</label>
        <input
          type="color"
          value={content.contentTextColor || '#333333'}
          onChange={(e) => updateContent('contentTextColor', e.target.value)}
          className="w-full p-1 border rounded h-10"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Content Font Size</label>
        <input
          type="text"
          value={content.contentFontSize || '16px'}
          onChange={(e) => updateContent('contentFontSize', e.target.value)}
          className="w-full p-2 border rounded"
          placeholder="16px, 1rem"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium mb-2">Content Alignment</label>
      <select
        value={content.contentAlignment || 'left'}
        onChange={(e) => updateContent('contentAlignment', e.target.value)}
        className="w-full p-2 border rounded"
      >
        <option value="left">Left</option>
        <option value="center">Center</option>
        <option value="right">Right</option>
        <option value="justify">Justify</option>
      </select>
    </div>
  </>
)}
          {content.popupType === 'iframe' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">iFrame URL</label>
                <input
                  type="url"
                  value={content.iframeUrl || ''}
                  onChange={(e) => updateContent('iframeUrl', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="https://example.com/embed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">iFrame Height</label>
                <input
                  type="text"
                  value={content.iframeHeight || '400px'}
                  onChange={(e) => updateContent('iframeHeight', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="400px, 50vh"
                />
              </div>
            </>
          )}

          {content.popupType === 'video' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Video URL</label>
                <input
                  type="url"
                  value={content.videoUrl || ''}
                  onChange={(e) => updateContent('videoUrl', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="https://example.com/video.mp4"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="videoAutoplay"
                    checked={content.videoAutoplay || false}
                    onChange={(e) => updateContent('videoAutoplay', e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="videoAutoplay" className="text-sm font-medium">
                    Auto Play Video
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="videoControls"
                    checked={content.videoControls !== false}
                    onChange={(e) => updateContent('videoControls', e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="videoControls" className="text-sm font-medium">
                    Show Video Controls
                  </label>
                </div>
              </div>
            </>
          )}

          {(content.popupType === 'image' || content.popupType === 'gallery') && (
            <div>
              <label className="block text-sm font-medium mb-2">
                {content.popupType === 'gallery' ? 'Gallery Images' : 'Image'}
              </label>
              
              {(!content.images || content.images.length === 0) && (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500 mb-4">No images added yet</p>
                  <button
                    type="button"
                    onClick={addImage}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center mx-auto"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Image
                  </button>
                </div>
              )}

              {content.images && content.images.map((image, index) => (
                <div key={index} className="border rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="font-medium">Image {index + 1}</h5>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3">
                  <div>
  <label className="block text-sm font-medium mb-1">Image URL</label>
  <input
    type="url"
    value={image.url || ''}
    onChange={(e) => updateImage(index, 'url', e.target.value)}
    className="w-full p-2 border rounded"
    placeholder="https://example.com/image.jpg"
  />
  <ImageUploader 
    onImageUpload={(url) => updateImage(index, 'url', url)}
    pageSlug="popup-images" 
  />
</div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Alt Text</label>
                      <input
                        type="text"
                        value={image.alt || ''}
                        onChange={(e) => updateImage(index, 'alt', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Image description"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Caption (Optional)</label>
                      <input
                        type="text"
                        value={image.caption || ''}
                        onChange={(e) => updateImage(index, 'caption', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Image caption"
                      />
                    </div>
                    
                    {image.url && (
                      <div className="mt-2">
                        <img 
                          src={image.url} 
                          alt={image.alt || 'Preview'} 
                          className="max-w-32 max-h-32 object-cover rounded border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {content.images && content.images.length > 0 && content.popupType === 'gallery' && (
                <button
                  type="button"
                  onClick={addImage}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 flex items-center justify-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Image
                </button>
              )}

              {content.popupType === 'image' && (!content.images || content.images.length === 0) && (
                <button
                  type="button"
                  onClick={addImage}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center justify-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Image
                </button>
              )}
            </div>
          )}

          {content.popupType === 'gallery' && content.images && content.images.length > 1 && (
            <div className="bg-white p-4 rounded border">
              <h5 className="font-medium mb-3">Gallery Settings</h5>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showGalleryNavigation"
                    checked={content.showGalleryNavigation !== false}
                    onChange={(e) => updateContent('showGalleryNavigation', e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="showGalleryNavigation" className="text-sm font-medium">
                    Show Navigation Arrows
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showGalleryDots"
                    checked={content.showGalleryDots || false}
                    onChange={(e) => updateContent('showGalleryDots', e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="showGalleryDots" className="text-sm font-medium">
                    Show Dots Navigation
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="galleryAutoplay"
                    checked={content.galleryAutoplay || false}
                    onChange={(e) => updateContent('galleryAutoplay', e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="galleryAutoplay" className="text-sm font-medium">
                    Auto Play Gallery
                  </label>
                </div>

                {content.galleryAutoplay && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Auto Play Speed (seconds)</label>
                    <input
                      type="number"
                      value={content.galleryAutoplaySpeed / 1000 || 5}
                      onChange={(e) => updateContent('galleryAutoplaySpeed', parseInt(e.target.value) * 1000)}
                      className="w-full p-2 border rounded"
                      min="1"
                      step="0.5"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Popup Dimensions & Layout */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <Settings className="w-4 h-4 mr-2" />
          Popup Dimensions & Layout
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Popup Width</label>
            <input
              type="text"
              value={content.popupWidth || '600px'}
              onChange={(e) => updateContent('popupWidth', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="600px, 50%, auto"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Popup Height</label>
            <input
              type="text"
              value={content.popupHeight || 'auto'}
              onChange={(e) => updateContent('popupHeight', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="auto, 400px, 50vh"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Max Width</label>
            <input
              type="text"
              value={content.popupMaxWidth || '90vw'}
              onChange={(e) => updateContent('popupMaxWidth', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="90vw, 800px"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Max Height</label>
            <input
              type="text"
              value={content.popupMaxHeight || '90vh'}
              onChange={(e) => updateContent('popupMaxHeight', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="90vh, 600px"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Min Width</label>
            <input
              type="text"
              value={content.popupMinWidth || '300px'}
              onChange={(e) => updateContent('popupMinWidth', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="300px, 20%"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Min Height</label>
            <input
              type="text"
              value={content.popupMinHeight || '200px'}
              onChange={(e) => updateContent('popupMinHeight', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="200px, 20vh"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Popup Position</label>
            <select
              value={content.popupPosition || 'center'}
              onChange={(e) => updateContent('popupPosition', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="center">Center</option>
              <option value="top">Top Center</option>
              <option value="bottom">Bottom Center</option>
              <option value="left">Left Center</option>
              <option value="right">Right Center</option>
              <option value="top-left">Top Left</option>
              <option value="top-right">Top Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="bottom-right">Bottom Right</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Z-Index</label>
            <input
              type="number"
              value={content.popupZIndex || 1000}
              onChange={(e) => updateContent('popupZIndex', parseInt(e.target.value))}
              className="w-full p-2 border rounded"
              min="1"
              max="9999"
            />
          </div>
        </div>
      </div>

      {/* Popup Styling & Colors */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <Palette className="w-4 h-4 mr-2" />
          Popup Styling & Colors
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Background Color</label>
            <input
              type="color"
              value={content.backgroundColor || '#ffffff'}
              onChange={(e) => updateContent('backgroundColor', e.target.value)}
              className="w-full p-1 border rounded h-10"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Text Color</label>
            <input
              type="color"
              value={content.textColor || '#333333'}
              onChange={(e) => updateContent('textColor', e.target.value)}
              className="w-full p-1 border rounded h-10"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Overlay Color</label>
            <input
              type="text"
              value={content.overlayColor || 'rgba(0, 0, 0, 0.8)'}
              onChange={(e) => updateContent('overlayColor', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="rgba(0, 0, 0, 0.8)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Border Color</label>
            <input
              type="color"
              value={content.borderColor || '#e5e7eb'}
              onChange={(e) => updateContent('borderColor', e.target.value)}
              className="w-full p-1 border rounded h-10"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Border Width</label>
            <input
              type="text"
              value={content.borderWidth || '0px'}
              onChange={(e) => updateContent('borderWidth', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="0px, 1px, 2px"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Border Style</label>
            <select
              value={content.borderStyle || 'solid'}
              onChange={(e) => updateContent('borderStyle', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="none">None</option>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
              <option value="double">Double</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Border Radius</label>
            <input
              type="text"
              value={content.borderRadius || '8px'}
              onChange={(e) => updateContent('borderRadius', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="8px, 1rem, 50%"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Box Shadow</label>
            <select
              value={content.boxShadow || 'large'}
              onChange={(e) => updateContent('boxShadow', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="none">No Shadow</option>
              <option value="small">Small Shadow</option>
              <option value="medium">Medium Shadow</option>
              <option value="large">Large Shadow</option>
              <option value="xl">Extra Large Shadow</option>
              <option value="custom">Custom Shadow</option>
            </select>
          </div>

          {content.boxShadow === 'custom' && (
            <div>
              <label className="block text-sm font-medium mb-2">Custom Shadow CSS</label>
              <input
                type="text"
                value={content.customBoxShadow || ''}
                onChange={(e) => updateContent('customBoxShadow', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="0 25px 50px rgba(0, 0, 0, 0.25)"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Padding</label>
            <input
              type="text"
              value={content.padding || '2rem'}
              onChange={(e) => updateContent('padding', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="2rem, 32px, 1rem 2rem"
            />
          </div>

          <div>
  <label className="block text-sm font-medium mb-2">Background Image</label>
  <input
    type="url"
    value={content.backgroundImage || ''}
    onChange={(e) => updateContent('backgroundImage', e.target.value)}
    className="w-full p-2 border rounded"
    placeholder="https://example.com/background.jpg"
  />
  <ImageUploader 
    onImageUpload={(url) => updateContent('backgroundImage', url)}
    pageSlug="popup-images" 
  />
</div>

          {content.backgroundImage && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Background Size</label>
                <select
                  value={content.backgroundSize || 'cover'}
                  onChange={(e) => updateContent('backgroundSize', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="cover">Cover</option>
                  <option value="contain">Contain</option>
                  <option value="auto">Auto</option>
                  <option value="100% 100%">Stretch</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Background Position</label>
                <select
                  value={content.backgroundPosition || 'center'}
                  onChange={(e) => updateContent('backgroundPosition', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="center">Center</option>
                  <option value="top">Top</option>
                  <option value="bottom">Bottom</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="top left">Top Left</option>
                  <option value="top right">Top Right</option>
                  <option value="bottom left">Bottom Left</option>
                  <option value="bottom right">Bottom Right</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Background Repeat</label>
                <select
                  value={content.backgroundRepeat || 'no-repeat'}
                  onChange={(e) => updateContent('backgroundRepeat', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="no-repeat">No Repeat</option>
                  <option value="repeat">Repeat</option>
                  <option value="repeat-x">Repeat X</option>
                  <option value="repeat-y">Repeat Y</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Background Overlay Opacity</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={content.backgroundOverlayOpacity || 0}
                  onChange={(e) => updateContent('backgroundOverlayOpacity', parseFloat(e.target.value))}
                  className="w-full"
                />
                <span className="text-sm text-gray-500">{content.backgroundOverlayOpacity || 0}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Typography Settings */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4">Typography Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Font Family</label>
            <select
              value={content.fontFamily || 'inherit'}
              onChange={(e) => updateContent('fontFamily', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="inherit">Inherit</option>
              <option value="Arial, sans-serif">Arial</option>
              <option value="'Helvetica Neue', sans-serif">Helvetica</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="'Courier New', monospace">Courier New</option>
              <option value="'Inter', sans-serif">Inter</option>
              <option value="'Roboto', sans-serif">Roboto</option>
              <option value="'Open Sans', sans-serif">Open Sans</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Font Size</label>
            <input
              type="text"
              value={content.fontSize || '16px'}
              onChange={(e) => updateContent('fontSize', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="16px, 1rem, 1.2em"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Font Weight</label>
            <select
              value={content.fontWeight || 'normal'}
              onChange={(e) => updateContent('fontWeight', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="100">Thin (100)</option>
              <option value="200">Extra Light (200)</option>
              <option value="300">Light (300)</option>
              <option value="normal">Normal (400)</option>
              <option value="500">Medium (500)</option>
              <option value="600">Semi Bold (600)</option>
              <option value="bold">Bold (700)</option>
              <option value="800">Extra Bold (800)</option>
              <option value="900">Black (900)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Line Height</label>
            <input
              type="text"
              value={content.lineHeight || '1.5'}
              onChange={(e) => updateContent('lineHeight', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="1.5, 24px, normal"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Letter Spacing</label>
            <input
              type="text"
              value={content.letterSpacing || 'normal'}
              onChange={(e) => updateContent('letterSpacing', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="normal, 0.5px, 0.05em"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Text Align</label>
            <select
              value={content.textAlign || 'left'}
              onChange={(e) => updateContent('textAlign', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
              <option value="justify">Justify</option>
            </select>
          </div>
        </div>
      </div>

      {/* Animation & Effects */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4">Animation & Effects</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Animation Type</label>
            <select
              value={content.animationType || 'fade'}
              onChange={(e) => updateContent('animationType', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="none">No Animation</option>
              <option value="fade">Fade In/Out</option>
              <option value="scale">Scale In/Out</option>
              <option value="slide">Slide Up/Down</option>
              <option value="slideLeft">Slide Left</option>
              <option value="slideRight">Slide Right</option>
              <option value="bounce">Bounce</option>
              <option value="rotate">Rotate</option>
              <option value="flip">Flip</option>
              <option value="zoom">Zoom</option>
              <option value="elastic">Elastic</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Animation Duration (ms)</label>
            <input
              type="number"
              value={content.animationDuration || 300}
              onChange={(e) => updateContent('animationDuration', parseInt(e.target.value))}
              className="w-full p-2 border rounded"
              min="100"
              max="3000"
              step="50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Animation Easing</label>
            <select
              value={content.animationEasing || 'ease'}
              onChange={(e) => updateContent('animationEasing', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="ease">Ease</option>
              <option value="ease-in">Ease In</option>
              <option value="ease-out">Ease Out</option>
              <option value="ease-in-out">Ease In Out</option>
              <option value="linear">Linear</option>
              <option value="cubic-bezier(0.4, 0, 0.2, 1)">Custom Cubic Bezier</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Animation Delay (ms)</label>
            <input
              type="number"
              value={content.animationDelay || 0}
              onChange={(e) => updateContent('animationDelay', parseInt(e.target.value))}
              className="w-full p-2 border rounded"
              min="0"
              max="5000"
              step="100"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableBlurEffect"
              checked={content.enableBlurEffect || false}
              onChange={(e) => updateContent('enableBlurEffect', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="enableBlurEffect" className="text-sm font-medium">
              Blur background when open
            </label>
          </div>

          {content.enableBlurEffect && (
            <div>
              <label className="block text-sm font-medium mb-2">Blur Amount (px)</label>
              <input
                type="number"
                value={content.blurAmount || 5}
                onChange={(e) => updateContent('blurAmount', parseInt(e.target.value))}
                className="w-full p-2 border rounded"
                min="1"
                max="20"
              />
            </div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableParallax"
              checked={content.enableParallax || false}
              onChange={(e) => updateContent('enableParallax', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="enableParallax" className="text-sm font-medium">
              Enable parallax scrolling
            </label>
          </div>

          {content.enableParallax && (
            <div>
              <label className="block text-sm font-medium mb-2">Parallax Speed</label>
              <input
                type="range"
                min="0.1"
                max="2"
                step="0.1"
                value={content.parallaxSpeed || 0.5}
                onChange={(e) => updateContent('parallaxSpeed', parseFloat(e.target.value))}
                className="w-full"
              />
              <span className="text-sm text-gray-500">{content.parallaxSpeed || 0.5}x</span>
            </div>
          )}
        </div>
      </div>

      {/* Behavior Settings */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4">Behavior Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="closeOnOverlayClick"
              checked={content.closeOnOverlayClick !== false}
              onChange={(e) => updateContent('closeOnOverlayClick', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="closeOnOverlayClick" className="text-sm font-medium">
              Close on overlay click
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="closeOnEscape"
              checked={content.closeOnEscape !== false}
              onChange={(e) => updateContent('closeOnEscape', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="closeOnEscape" className="text-sm font-medium">
              Close on Escape key
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="showCloseButton"
              checked={content.showCloseButton !== false}
              onChange={(e) => updateContent('showCloseButton', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="showCloseButton" className="text-sm font-medium">
              Show close button (X)
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="preventBodyScroll"
              checked={content.preventBodyScroll !== false}
              onChange={(e) => updateContent('preventBodyScroll', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="preventBodyScroll" className="text-sm font-medium">
              Prevent body scroll when open
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="centerOnResize"
              checked={content.centerOnResize !== false}
              onChange={(e) => updateContent('centerOnResize', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="centerOnResize" className="text-sm font-medium">
              Re-center on window resize
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="closeOnClickInside"
              checked={content.closeOnClickInside || false}
              onChange={(e) => updateContent('closeOnClickInside', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="closeOnClickInside" className="text-sm font-medium">
              Close when clicking inside popup
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Auto Close After (seconds)</label>
            <input
              type="number"
              value={content.autoCloseAfter || 0}
              onChange={(e) => updateContent('autoCloseAfter', parseInt(e.target.value))}
              className="w-full p-2 border rounded"
              min="0"
              placeholder="0 = Never auto close"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Max Opens Per Session</label>
            <input
              type="number"
              value={content.maxOpensPerSession || 0}
              onChange={(e) => updateContent('maxOpensPerSession', parseInt(e.target.value))}
              className="w-full p-2 border rounded"
              min="0"
              placeholder="0 = Unlimited"
            />
          </div>
        </div>
      </div>

      {/* Responsive Settings */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4">Responsive Settings</h4>
        
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableResponsive"
              checked={content.enableResponsive !== false}
              onChange={(e) => updateContent('enableResponsive', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="enableResponsive" className="text-sm font-medium">
              Enable responsive behavior
            </label>
          </div>

          {content.enableResponsive !== false && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Mobile Width</label>
                  <input
                    type="text" 
                    value={content.mobileWidth || '95vw'}
                    onChange={(e) => updateContent('mobileWidth', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="95vw, 350px"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Mobile Height</label>
                  <input
                    type="text"
                    value={content.mobileHeight || 'auto'}
                    onChange={(e) => updateContent('mobileHeight', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="auto, 80vh"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Mobile Padding</label>
                  <input
                    type="text"
                    value={content.mobilePadding || '1rem'}
                    onChange={(e) => updateContent('mobilePadding', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="1rem, 16px"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Mobile Font Size</label>
                  <input
                    type="text"
                    value={content.mobileFontSize || '14px'}
                    onChange={(e) => updateContent('mobileFontSize', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="14px, 0.875rem"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="fullscreenOnMobile"
                  checked={content.fullscreenOnMobile || false}
                  onChange={(e) => updateContent('fullscreenOnMobile', e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="fullscreenOnMobile" className="text-sm font-medium">
                  Full screen on mobile devices
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4">Advanced Settings</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Custom CSS Classes</label>
            <input
              type="text"
              value={content.customCssClasses || ''}
              onChange={(e) => updateContent('customCssClasses', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="custom-popup my-class"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Custom CSS Styles</label>
            <textarea
              value={content.customCssStyles || ''}
              onChange={(e) => updateContent('customCssStyles', e.target.value)}
              className="w-full p-2 border rounded"
              rows="4"
              placeholder="transform: rotate(5deg); backdrop-filter: blur(10px);"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Custom HTML Attributes</label>
            <textarea
              value={content.customAttributes || ''}
              onChange={(e) => updateContent('customAttributes', e.target.value)}
              className="w-full p-2 border rounded"
              rows="2"
              placeholder='data-analytics="popup-newsletter" role="dialog"'
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableAnalytics"
              checked={content.enableAnalytics || false}
              onChange={(e) => updateContent('enableAnalytics', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="enableAnalytics" className="text-sm font-medium">
              Enable analytics tracking
            </label>
          </div>

          {content.enableAnalytics && (
            <div>
              <label className="block text-sm font-medium mb-2">Analytics Event Name</label>
              <input
                type="text"
                value={content.analyticsEvent || 'popup_opened'}
                onChange={(e) => updateContent('analyticsEvent', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="popup_opened"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PopupForm;